import { useMemo } from 'react'
import { groupImagesByCategory } from '../category-tabs'
import { CategoryBrowseTab } from '../types'
import type { CategoryTab, CategoryTabOption, Image, ImageCategoryTabOption } from '../types'
import { useTranslation } from '../../../../../hooks/locale'

export interface GallerySection {
    key: CategoryTab
    title: string
    images: Image[]
}

interface UseGallerySectionsProps {
    images: Image[]
    enableCategorizedGallery: boolean
    availableTabs: CategoryTabOption[]
    activeCategoryTab: CategoryTab
}

export const useGallerySections = ({
    images,
    enableCategorizedGallery,
    availableTabs,
    activeCategoryTab,
}: UseGallerySectionsProps) => {
    const { t } = useTranslation()
    const videoSectionTitle = t('gallery.videos')

    const imageSections = useMemo<GallerySection[]>(() => {
        if (!enableCategorizedGallery) {
            return [{ key: CategoryBrowseTab.ALL, title: t('gallery.photos'), images }]
        }

        if (activeCategoryTab !== CategoryBrowseTab.ALL) {
            const activeTab = availableTabs.find(tabOption => tabOption.key === activeCategoryTab)
            return [
                {
                    key: activeCategoryTab,
                    title: activeTab ? t(activeTab.translationKey) : t('gallery.photos'),
                    images,
                },
            ]
        }

        const imagesByCategory = groupImagesByCategory(images)

        return availableTabs
            .filter(
                (tabOption): tabOption is ImageCategoryTabOption =>
                    tabOption.key !== CategoryBrowseTab.ALL && tabOption.key !== CategoryBrowseTab.VIDEO,
            )
            .map(tabOption => ({
                key: tabOption.key,
                title: t(tabOption.translationKey),
                images: imagesByCategory.get(tabOption.key) ?? [],
            }))
    }, [enableCategorizedGallery, activeCategoryTab, availableTabs, images, t])

    return { videoSectionTitle, imageSections }
}
